import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Close from '@mui/icons-material/Close';

interface Option {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: Option[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const Container = styled.div<{ disabled?: boolean }>`
  position: relative;
  width: 100%;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.7 : 1};
`;

const SelectBox = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background: #f9fafb;
  border: 1px solid ${props => props.isOpen ? 'var(--primary-orange)' : '#e5e7eb'};
  border-radius: 10px;
  min-height: 42px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.isOpen ? 'var(--primary-orange)' : '#d1d5db'};
  }
`;

const SelectedItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  flex: 1;
`;

const Tag = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: var(--primary-orange);
  color: white;
  padding: 0.1rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const RemoveButton = styled.span`
  display: flex;
  align-items: center;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
  svg {
    font-size: 0.875rem;
  }
`;

const Placeholder = styled.span`
  color: #9ca3af;
  font-size: 0.875rem;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 250px;
  overflow-y: auto;
`;

const OptionItem = styled.div<{ isSelected: boolean }>`
  padding: 0.6rem 0.75rem;
  font-size: 0.875rem;
  color: ${props => props.isSelected ? 'var(--primary-orange)' : '#374151'};
  background: ${props => props.isSelected ? 'rgba(255, 122, 0, 0.05)' : 'transparent'};
  font-weight: ${props => props.isSelected ? '700' : '400'};
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }

  &:first-child {
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }

  &:last-child {
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
  }
`;

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = 'Select options...',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (value: string) => {
    if (disabled) return;
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  const removeValue = (e: React.MouseEvent, value: string) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(selectedValues.filter(v => v !== value));
  };

  return (
    <Container ref={containerRef} disabled={disabled}>
      <SelectBox isOpen={isOpen} onClick={() => !disabled && setIsOpen(!isOpen)}>
        <SelectedItems>
          {selectedValues.length > 0 ? (
            selectedValues.map(val => {
              const option = options.find(o => o.value === val);
              return (
                <Tag key={val}>
                  {option ? option.label : val}
                  <RemoveButton onClick={(e) => removeValue(e, val)}>
                    <Close />
                  </RemoveButton>
                </Tag>
              );
            })
          ) : (
            <Placeholder>{placeholder}</Placeholder>
          )}
        </SelectedItems>
        <KeyboardArrowDown style={{ color: '#9ca3af', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
      </SelectBox>
      {isOpen && (
        <Dropdown>
          {options.map(opt => (
            <OptionItem
              key={opt.value}
              isSelected={selectedValues.includes(opt.value)}
              onClick={() => toggleOption(opt.value)}
            >
              {opt.label}
            </OptionItem>
          ))}
        </Dropdown>
      )}
    </Container>
  );
};

export default MultiSelect;
